import RPMDateTimePicker from '@/components/RPMDateTimePicker/RPMDateTimePicker';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconChevronLeft, IconChevronRight } from '@/theme/icons';
import { isThisWeek } from '@/utils/calendar';
import { getDatePickerBorderColor } from '@/utils/color';
import rem from '@/utils/rem';
import {
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  StackProps,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { addDays, addWeeks, isToday, subDays, subWeeks } from 'date-fns';
import { useCallback } from 'react';
import { Navigate } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';

import InnerDate from './innerDate';

type Props = {
  selectedDate: Date;
  showCalendarIcon?: boolean;
  showDatepicker?: boolean;
  onCategorySpotlight?: boolean;
  isInDayMode?: boolean;
} & StackProps;

const DayWeekNavigator = ({
  selectedDate,
  showCalendarIcon,
  showDatepicker = false,
  onCategorySpotlight,
  isInDayMode = false,
  ...stackProps
}: Props) => {
  const { t } = useTranslation('DayWeekNavigator');
  const updateSelectedDate = useCalendarMonthlyStore((state) => state.updateSelectedDate);
  const isDailyPlanPage = useIsDailyPlanPage(isInDayMode);
  const isInDailyMode = isDailyPlanPage;
  const isCurrentWeek = isThisWeek(selectedDate);
  const isCurrentDay = isToday(selectedDate);
  const isCurrent = (!isInDailyMode && isCurrentWeek) || (isInDailyMode && isCurrentDay);
  const borderColor = getDatePickerBorderColor(selectedDate, isCurrent);

  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure({ id: 'datepicker-secondary-navigation' });

  const onHandlePrevNextCalendarNavigation = useCallback(
    (date: Date, action: 'PREV' | 'NEXT' | 'TODAY') => {
      switch (action) {
        case Navigate.PREVIOUS: {
          const newDate = !isInDailyMode ? subWeeks(date, 1) : subDays(date, 1);
          updateSelectedDate(newDate);

          return newDate;
        }

        case Navigate.NEXT: {
          const newDate = !isInDailyMode ? addWeeks(date, 1) : addDays(date, 1);
          updateSelectedDate(newDate);

          return newDate;
        }

        default: {
          updateSelectedDate(date);
          return date;
        }
      }
    },
    [updateSelectedDate, isDailyPlanPage],
  );

  const onSelectedDate = useCallback(
    (selectedDate: Date) => {
      updateSelectedDate(selectedDate);
      onDatePickerClose();
    },
    [onDatePickerClose, updateSelectedDate],
  );

  if (showDatepicker) {
    return (
      <HStack
        position="relative"
        justifyContent="center"
        gap={rem(5)}
        width="fit-content"
        minWidth="fit-content"
        height="full"
        {...stackProps}
      >
        <Tooltip label={!isInDailyMode ? t('tooltips.previousWeek') : t('tooltips.previousDay')} placement="top">
          <IconButton
            width={rem(32)}
            minWidth={0}
            height="full"
            margin={0}
            padding={0}
            borderWidth={rem(1)}
            borderStyle="solid"
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            aria-label={!isInDailyMode ? t('ariaLabels.previousWeek') : t('ariaLabels.previousDay')}
            backgroundColor="background-primary"
            icon={<Icon as={IconChevronLeft} boxSize={rem(14)} />}
            onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.PREVIOUS)}
            size="xs"
            variant="ghost"
          />
        </Tooltip>

        <Popover closeOnBlur closeOnEsc isLazy isOpen={isDatePickerOpen} onClose={onDatePickerClose} placement="bottom">
          <PopoverTrigger>
            <InnerDate
              selectedDate={selectedDate}
              showCalendarIcon={showCalendarIcon}
              showDatePicker={showDatepicker}
              onDatePickerOpen={onDatePickerOpen}
              borderColor={borderColor}
              onCategorySpotlight={onCategorySpotlight}
              isInDayMode={isInDayMode}
            />
          </PopoverTrigger>
          <PopoverContent
            width={rem(400)}
            padding={rem(16)}
            border={`${rem(1)} solid`}
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            backgroundColor="background-primary"
          >
            <PopoverBody className={!isInDailyMode ? 'week-style' : 'day-style'} padding={0}>
              <RPMDateTimePicker
                dateRange={!isInDailyMode}
                selectedDate={selectedDate}
                updateSelectedDate={onSelectedDate}
                withActionButtons={false}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <Tooltip label={!isInDailyMode ? t('tooltips.nextWeek') : t('tooltips.nextDay')} placement="top">
          <IconButton
            width={rem(32)}
            minWidth={0}
            height="full"
            margin={0}
            padding={0}
            borderWidth={rem(1)}
            borderStyle="solid"
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            aria-label={!isInDailyMode ? t('ariaLabels.nextWeek') : t('ariaLabels.nextDay')}
            backgroundColor="background-primary"
            icon={<Icon as={IconChevronRight} boxSize={rem(14)} />}
            onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.NEXT)}
            size="xs"
            variant="ghost"
          />
        </Tooltip>
      </HStack>
    );
  }

  return (
    <HStack
      position="relative"
      justifyContent="center"
      gap={rem(5)}
      width="fit-content"
      minWidth="fit-content"
      height="full"
      {...stackProps}
    >
      <Tooltip label={!isInDailyMode ? t('tooltips.previousWeek') : t('tooltips.previousDay')} placement="top">
        <IconButton
          width={rem(32)}
          minWidth={0}
          height="full"
          margin={0}
          padding={0}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor="stroke-primary"
          borderRadius={rem(4)}
          aria-label={!isInDailyMode ? t('ariaLabels.previousWeek') : t('ariaLabels.previousDay')}
          aspectRatio={1}
          backgroundColor="background-primary"
          icon={<Icon as={IconChevronLeft} boxSize={rem(14)} />}
          onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.PREVIOUS)}
          size="xs"
          variant="ghost"
        />
      </Tooltip>

      <InnerDate
        selectedDate={selectedDate}
        showCalendarIcon={showCalendarIcon}
        showDatePicker={showDatepicker}
        borderColor={borderColor}
        isInDayMode={isInDayMode}
      />

      <Tooltip label={!isInDailyMode ? t('tooltips.nextWeek') : t('tooltips.nextDay')} placement="top">
        <IconButton
          width={rem(32)}
          minWidth={0}
          height="full"
          margin={0}
          padding={0}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor="stroke-primary"
          borderRadius={rem(4)}
          aria-label={!isInDailyMode ? t('ariaLabels.nextWeek') : t('ariaLabels.nextDay')}
          aspectRatio={1}
          backgroundColor="background-primary"
          icon={<Icon as={IconChevronRight} boxSize={rem(14)} />}
          onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, Navigate.NEXT)}
          size="xs"
          variant="ghost"
        />
      </Tooltip>
    </HStack>
  );
};

export default DayWeekNavigator;
